interface Coordinates {
  lat: number;
  lng: number;
}

// Base coordinates for Heffners Allé 43, Sundsvall
export const BASE_COORDINATES: Coordinates = {
  lat: 62.390511,
  lng: 17.304375
};

// Known locations as fallback
const KNOWN_LOCATIONS: { [key: string]: Coordinates } = {
  'kvissleby': { lat: 62.2969, lng: 17.3741 },
  'sundsvall': { lat: 62.3908, lng: 17.3069 },
  'sollefteå': { lat: 63.1687, lng: 17.2666 }
};

// Cache structure with timestamps
interface CacheEntry {
  coordinates: Coordinates;
  timestamp: number;
}

// Load cached coordinates from localStorage
const CACHE_KEY = 'geocode_cache_v2';
const CACHE_EXPIRY = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

const geocodeCache: { [address: string]: CacheEntry } = (() => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return {};
    
    const parsed = JSON.parse(cached);
    const now = Date.now();
    
    // Clean expired entries
    Object.keys(parsed).forEach(key => {
      if (now - parsed[key].timestamp > CACHE_EXPIRY) {
        delete parsed[key];
      }
    });
    
    return parsed;
  } catch (e) {
    return {};
  }
})();

// Save coordinates to localStorage
function saveToCache(address: string, coordinates: Coordinates) {
  const entry: CacheEntry = {
    coordinates,
    timestamp: Date.now()
  };
  
  geocodeCache[address] = entry;
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(geocodeCache));
  } catch (e) {
    console.warn('Failed to save to localStorage:', e);
  }
}

// Queue for geocoding requests
// Thrown by performGeocoding on a transient failure (429 / 5xx) so the queue
// can back off and retry rather than treating it as "no location". (#39)
class GeocodingTransientError extends Error {
  constructor(public status: number) {
    super(`Geocoding transient failure (HTTP ${status})`);
    this.name = 'GeocodingTransientError';
  }
}

let geocodingQueue: Array<{
  address: string;
  resolve: (coords: Coordinates | null) => void;
  attempts?: number;
}> = [];
let isProcessingQueue = false;

const GEOCODING_BASE_DELAY_MS = 1000; // Nominatim allows ~1 req/sec.
const GEOCODING_MAX_ATTEMPTS = 4;     // ~1s, 2s, 4s backoff then give up.

// Process the geocoding queue
async function processQueue() {
  if (isProcessingQueue || geocodingQueue.length === 0) return;

  isProcessingQueue = true;

  while (geocodingQueue.length > 0) {
    const request = geocodingQueue.shift();
    if (!request) continue;

    try {
      const coords = await performGeocoding(request.address);
      request.resolve(coords);
      await new Promise(resolve => setTimeout(resolve, GEOCODING_BASE_DELAY_MS));
    } catch (error) {
      if (error instanceof GeocodingTransientError) {
        const attempts = (request.attempts ?? 0) + 1;
        if (attempts < GEOCODING_MAX_ATTEMPTS) {
          // Exponential backoff, then retry the same address so a rate-limit
          // burst doesn't permanently drop its distance.
          const backoff = GEOCODING_BASE_DELAY_MS * Math.pow(2, attempts - 1);
          console.warn(`Geocoding rate-limited (HTTP ${error.status}); retry ${attempts}/${GEOCODING_MAX_ATTEMPTS} after ${backoff}ms`);
          geocodingQueue.push({ ...request, attempts });
          await new Promise(resolve => setTimeout(resolve, backoff));
          continue;
        }
        console.warn(`Geocoding gave up after ${GEOCODING_MAX_ATTEMPTS} rate-limited attempts for "${request.address}"`);
        request.resolve(null);
      } else {
        console.error('Error processing geocoding request:', error);
        request.resolve(null);
      }
    }
  }

  isProcessingQueue = false;
}

async function performGeocoding(address: string): Promise<Coordinates | null> {
  try {
    const params = new URLSearchParams({
      format: 'json',
      q: `${address}, Sverige`,
      countrycodes: 'se',
      addressdetails: '1',
      limit: '1',
      'accept-language': 'sv'
    });

    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?${params.toString()}`
    );

    // Distinguish transient failures (429 rate-limit, 5xx) from a genuine
    // "no result". The old `if (!response.ok) return null` cached a
    // rate-limited address as if it had no location, so a burst of lookups on
    // a busy list silently lost every distance. Throw on transient so the
    // queue can back off and retry instead of resolving null. (#39)
    if (response.status === 429 || response.status >= 500) {
      throw new GeocodingTransientError(response.status);
    }
    if (!response.ok) return null;

    const data = await response.json();
    if (!data?.[0]) return null;

    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon)
    };
  } catch (error) {
    console.error('Geocoding error:', error);
    return null;
  }
}

// Add a function to save coordinates directly from Google Maps
export function saveCoordinatesFromGoogleMaps(address: string, coordinates: Coordinates) {
  saveToCache(address.toLowerCase(), coordinates);
}

export async function geocodeAddress(street: string, city: string): Promise<Coordinates | null> {
  const addressKey = `${street}, ${city}`.toLowerCase();
  
  // Check cache first
  if (geocodeCache[addressKey]) {
    return geocodeCache[addressKey].coordinates;
  }

  // Check known locations
  const cityKey = city.toLowerCase();
  if (KNOWN_LOCATIONS[cityKey]) {
    const coords = KNOWN_LOCATIONS[cityKey];
    saveToCache(addressKey, coords);
    return coords;
  }

  // Add to queue and wait for result
  return new Promise((resolve) => {
    geocodingQueue.push({
      address: addressKey,
      resolve: (coords) => {
        if (coords) saveToCache(addressKey, coords);
        resolve(coords);
      }
    });
    
    // Start processing queue if not already running
    processQueue();
  });
}

// Haversine formula for calculating distance between two points on Earth
export function calculateDistance(coord1: Coordinates, coord2: Coordinates): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = toRad(coord2.lat - coord1.lat);
  const dLon = toRad(coord2.lng - coord1.lng);
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(toRad(coord1.lat)) * Math.cos(toRad(coord2.lat)) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const straightLineDistance = R * c;
  
  // Apply a multiplier to approximate driving distance
  // 1.4 is a typical multiplier for driving distance vs straight line
  const approximateDrivingDistance = straightLineDistance * 1.4;
  
  return Math.round(approximateDrivingDistance); // Round to whole kilometers for driving distance
}

function toRad(degrees: number): number {
  return degrees * (Math.PI / 180);
}

// Format distance for display
export function formatDistance(distance: number | null): string {
  if (distance === null) return 'Avstånd saknas';
  return `Ca: ${distance} km väg`; // Added "väg" to indicate it's road distance
} 
/**
 * Swedish postcode for a street + city, from Nominatim's address details.
 * Returns "851 71" style or null. Separate from the distance queue: it is
 * called once, on demand, from the address-fix action.
 */
export async function lookupPostalCode(street: string, city: string): Promise<string | null> {
  const q = `${street.trim()}, ${city.trim()}, Sverige`;
  if (!street.trim()) return null;
  try {
    const params = new URLSearchParams({ format: 'json', q, countrycodes: 'se', addressdetails: '1', limit: '1', 'accept-language': 'sv' });
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
    if (!res.ok) return null;
    const data = await res.json();
    const raw: string | undefined = data?.[0]?.address?.postcode;
    if (!raw) return null;
    const digits = raw.replace(/\D/g, '');
    return digits.length === 5 ? `${digits.slice(0, 3)} ${digits.slice(3)}` : raw;
  } catch (e) {
    console.warn('lookupPostalCode failed:', e);
    return null;
  }
}
